import React from 'react';
import cn from 'classnames';
import styles from './index.module.scss';

/**
 * WCAG 2.2 AA compliant anchor.
 * - Underlined by default so links are distinguishable without relying on color alone (SC 1.4.1).
 * - Visible focus indicator (SC 2.4.7 / 2.4.11).
 * - Links that open a new tab get a visually-hidden suffix announcing it (SC 3.2.5) and
 *   automatically receive rel="noopener noreferrer" for security.
 *
 * @param {import('react').AnchorHTMLAttributes<HTMLAnchorElement> & {
 *  tone?: 'default' | 'muted' | 'inherit'
 *  className?: string
 *  children?: React.ReactNode
 * }} props
 */
export default function Anchor({
  tone = 'default',
  target = undefined,
  rel = '',
  className = '',
  children = null,
  ...htmlProps
}) {
  const opensNewTab = target === '_blank';
  const anchorRel = opensNewTab
    ? [...new Set(['noopener', 'noreferrer', ...rel.split(' ')])]
        .filter(Boolean)
        .join(' ')
    : rel || undefined;

  return (
    <a
      {...htmlProps}
      target={target}
      rel={anchorRel}
      data-tone={tone}
      className={cn(styles.anchor, className)}
    >
      {children}
      {opensNewTab && (
        <span className={styles.visuallyHidden}>(opens in a new tab)</span>
      )}
    </a>
  );
}
